/**
 * The pause menu's Save & Load rows (spec §4.1, D-617 Q5), pure: slot rows over the station Saves tab's model
 * (`saveRow`), with Save (manual slots), Load and Export as left/right actions. Manual save follows `canSaveNow` (a
 * career exists and storage works) plus a combat guard ("Can't save in combat"). Also the quit confirm's
 * "Last save 2 min ago" line. Text comes from `hud/menu.json`.
 */
import type { SlotAction, SlotItem } from '../../hud/menu'
import menuText from '../../hud/menu.json'
import type { M2Data } from '../../data/m2Schemas'
import type { SaveSlotInfo, UiState } from '../../ui/contracts'
import { newestValid, orderedSlots, saveRow, type SaveRow } from '../../ui/models/saves'

const S = menuText.saves

/** `{name}` placeholders → values. */
export function fill(template: string, vars: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (all, key: string) => (key in vars ? String(vars[key]) : all))
}

type SaveState = Pick<UiState, 'saves' | 'storage' | 'career'>

/** Why a manual save is refused right now, or null when it is allowed. */
export function saveBlockReason(state: SaveState, inCombat: boolean): string | null {
  if (state.career === null) return S.noCareer
  if (state.storage === 'none') return S.noStorage
  if (inCombat) return S.inCombat
  return null
}

function actions(row: SaveRow, blocked: string | null): SlotAction[] {
  const out: SlotAction[] = []
  if (row.manual) out.push({ id: 'save', label: S.save, enabled: blocked === null, reason: blocked })
  const loadReason = row.status === 'empty' ? S.emptySlot : S.unreadable
  out.push({ id: 'load', label: S.load, enabled: row.canLoad, reason: row.canLoad ? null : loadReason })
  out.push({ id: 'export', label: S.export, enabled: row.canExport, reason: row.canExport ? null : S.emptySlot })
  return out
}

/** The six slot rows (manual, then autosaves) for the menu's saves screen. */
export function menuSaveSlots(data: Pick<M2Data, 'stations'>, state: SaveState, inCombat: boolean): SlotItem[] {
  const blocked = saveBlockReason(state, inCombat)
  return orderedSlots(state.saves).map((info) => {
    const row = saveRow(data, info, blocked === null)
    // The date leads so the row ellipsis (narrow panels) cuts the summary's tail, never the date (W1 polish).
    const when = info.status === 'ok' ? compactSavedAt(info.savedAt) : ''
    const meta = row.error ?? row.summary
    return { kind: 'slot', id: `slot:${row.slot}`, label: row.label, hint: when === '' ? meta : `${when} · ${meta}`, actions: actions(row, blocked) }
  })
}

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** "9 Oct 14:32" in local time (template and month names in menu.json), or '' without a valid timestamp. */
export function compactSavedAt(iso: string | undefined): string {
  const t = iso === undefined ? Number.NaN : Date.parse(iso)
  if (!Number.isFinite(t)) return ''
  const d = new Date(t)
  return fill(S.when, { day: d.getDate(), month: S.months[d.getMonth()] ?? '', hh: pad2(d.getHours()), mm: pad2(d.getMinutes()) })
}

/** The hint line under the list for a focused slot action: what Enter does, or why it can't. */
export function slotActionHint(action: SlotAction): string {
  if (!action.enabled) return action.reason ?? ''
  return S.actionHints[action.id]
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** "just now", "2 min ago", "3 h ago", "2 d ago". */
export function agoText(ms: number): string {
  if (ms < MINUTE) return S.ago.now
  if (ms < HOUR) return fill(S.ago.minutes, { n: Math.floor(ms / MINUTE) })
  if (ms < DAY) return fill(S.ago.hours, { n: Math.floor(ms / HOUR) })
  return fill(S.ago.days, { n: Math.floor(ms / DAY) })
}

/** "Last save 2 min ago" from the newest valid save, or "No save yet". */
export function lastSaveLine(saves: readonly SaveSlotInfo[], nowMs: number): string {
  const newest = newestValid(saves)
  const t = newest?.savedAt === undefined ? Number.NaN : Date.parse(newest.savedAt)
  if (!Number.isFinite(t)) return S.never
  return fill(S.lastSave, { ago: agoText(Math.max(0, nowMs - t)) })
}
